import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { releasesAPI, CreateReleaseRequest, UpdateReleaseRequest } from '../api';
import { toast } from 'react-toastify';
import { useT } from '../../../i18n';

export const useGetReleases = (search?: string) => {
  return useQuery({
    queryKey: ['releases', search],
    queryFn: () => releasesAPI.getAllReleases(search),
    select: (data) => data.data,
  });
};

export const useGetReleaseById = (id: string) => {
  return useQuery({
    queryKey: ['release', id],
    queryFn: () => releasesAPI.getReleaseById(id),
    select: (data) => data.data,
    enabled: !!id,
  });
};

export const useCreateRelease = () => {
  const t = useT();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateReleaseRequest) => releasesAPI.createRelease(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['releases'] });
      toast.success(t('releases.toast.created'));
    },
    onError: (error: any) => {
      const message = t('releases.toast.createFailed');
      toast.error(message);
    },
  });
};

export const useUpdateRelease = () => {
  const t = useT();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateReleaseRequest }) =>
      releasesAPI.updateRelease(id, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['releases'] });
      queryClient.invalidateQueries({ queryKey: ['release'] });
      toast.success(t('releases.toast.updated'));
    },
    onError: (error: any) => {
      const message = t('releases.toast.updateFailed');
      toast.error(message);
    },
  });
};

export const useDeleteRelease = () => {
  const t = useT();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => releasesAPI.deleteRelease(id),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['releases'] });
      toast.success(t('releases.toast.deleted'));
    },
    onError: (error: any) => {
      const message = t('releases.toast.deleteFailed');
      toast.error(message);
    },
  });
};

export const useDownloadRelease = () => {
  const t = useT();
  return useMutation({
    mutationFn: (id: string) => releasesAPI.downloadRelease(id),
    onSuccess: (response) => {
      // Open download URL in new tab
      if (response.data?.downloadUrl) {
        window.open(response.data.downloadUrl, '_blank');
      }
      toast.success(t('releases.toast.downloadStarted'));
    },
    onError: (error: any) => {
      const message = t('releases.toast.downloadFailed');
      toast.error(message);
    },
  });
};
